/**
 * How tall a box somebody types a message into should be.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ONE LINE, THEN AS TALL AS WHAT IS IN IT, THEN IT STOPS.
 *
 * The owner's words, 2026-08-20: "fix write box to same height as attachment and
 * send buttons, and only expand if it grows big". The chat composer was
 * `rows={2}` with `min-h-16` — four rem, twice the height of the `h-8` buttons
 * beside it — so an empty box shouted for attention on a phone screen where the
 * transcript is the thing worth space.
 *
 * WHY ROWS ARE COUNTED RATHER THAN MEASURED. The obvious implementation reads
 * `scrollHeight` in an effect and writes `style.height`. That needs LAYOUT, and
 * layout is exactly what jsdom does not do: `scrollHeight` is 0 in every test,
 * so an auto-grow built that way is a feature no test can see working — which is
 * how a feature ships that never worked (`printed_signature` in
 * packages/types/dist, release-two.md). Counting the lines the reader has
 * actually typed is arithmetic, so the growth is provable and the cap is
 * provably a cap.
 *
 * WRAPPING IS HANDLED BY CSS, NOT HERE, and deliberately. A long line with no
 * newline in it needs the browser's own measurement, which `field-sizing-content`
 * (already on `ui/textarea.tsx`) does natively; {@link COMPOSER_BOX_CLASS} caps
 * that growth with `max-h-32` and lets the box scroll inside itself. So the two
 * kinds of growth are handled by the two mechanisms that can actually see them,
 * and NEITHER can push Send off the screen.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * The tallest a compose box gets before it scrolls inside itself instead.
 *
 * SIX RATHER THAN "AS TALL AS IT LIKES", because the failure this bounds is
 * specific: a pasted purchase order or a forwarded specification is hundreds of
 * lines, and an unbounded box puts the Send button below the fold on a phone —
 * the reader then cannot send what they just pasted, which reads as the app
 * being broken rather than as a box being tall.
 *
 * SIX RATHER THAN THREE because the message this is most often used for is a
 * short paragraph, and a box that starts scrolling at three lines hides the
 * beginning of the sentence being written.
 */
export const COMPOSER_MAX_ROWS = 6

/**
 * The `rows` a compose box should carry for this text.
 *
 * One for empty or a single line — the same height as the `h-8` buttons beside
 * it, which is the whole ask — then one per hard line break, then it stops.
 *
 * A TRAILING NEWLINE COUNTS. `"hello\n"` is two lines and the second is where
 * the cursor is; not counting it would put the caret on a row the box has not
 * grown to show.
 */
export function composerRows(value: string): number {
  const lines = value.split("\n").length
  if (lines <= 1) return 1
  return Math.min(COMPOSER_MAX_ROWS, lines)
}

/**
 * The classes that make a `ui/textarea` behave as a compose box.
 *
 * `min-h-8` MATCHES `size="default"` ON `ui/button.tsx` (`h-8`) — that equality
 * is the owner's actual request and is asserted in the tests rather than left as
 * a coincidence of two files. `py-1` is what lets a single row FIT in 2rem;
 * the base class's `py-2` plus a line box overflows it and the box renders
 * taller than the buttons however small `min-h` is set.
 *
 * `max-h-32` + `overflow-y-auto` are the cap: past six lines (or a very long
 * wrapped one) the text scrolls INSIDE the box and the buttons stay put.
 *
 * `resize-none` because the drag handle would fight both of the above, and on a
 * touch screen it is a target nobody wants to hit.
 */
export const COMPOSER_BOX_CLASS = "max-h-32 min-h-8 resize-none overflow-y-auto py-1"
